import { inject, Injectable } from '@angular/core';
import { AuthNavigationService } from '../../../auth/services/auth-navigation/auth-navigation.service';
import { AppRouteNavigationService } from '../../../core/services/app-route-navigation.service';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { SPONSOR_ROUTES } from '../../constants/sponsor-routes.constants';
import { SponsorOffersService } from './sponsor-offers.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorOfferClaimNavigationService {
  private readonly appRouteNavigation = inject(AppRouteNavigationService);
  private readonly authNavigation = inject(AuthNavigationService);
  private readonly authService = inject(AuthService);

  public buildClaimPath(shortId: string): string {
    const normalizedShortId = shortId.trim();
    return `${SPONSOR_ROUTES.offerClaimBase}/${normalizedShortId}`;
  }

  public buildLegacyOfferPath(shortId: string): string {
    const normalizedShortId = shortId.trim();
    return `${SPONSOR_ROUTES.legacyOfferBase}/${normalizedShortId}`;
  }

  public isOfferClaimPath(url: string | null | undefined): boolean {
    const safeUrl = this.authService.sanitizePostLoginRedirect(url ?? null);
    if (!safeUrl) {
      return false;
    }
    return (
      safeUrl.startsWith(`${SPONSOR_ROUTES.offerClaimBase}/`) ||
      new RegExp(`^${SPONSOR_ROUTES.legacyOfferBase}/[^/]+$`).test(safeUrl)
    );
  }

  public extractShortIdFromClaimPath(url: string | null | undefined): string | null {
    const safeUrl = this.authService.sanitizePostLoginRedirect(url ?? null);
    if (!safeUrl) {
      return null;
    }

    const sponsorMatch = safeUrl.match(
      new RegExp(`^${SPONSOR_ROUTES.offerClaimBase}/([^/]+)$`),
    );
    if (sponsorMatch?.[1]) {
      return sponsorMatch[1].trim();
    }

    const legacyMatch = safeUrl.match(new RegExp(`^${SPONSOR_ROUTES.legacyOfferBase}/([^/]+)$`));
    if (legacyMatch?.[1]) {
      return legacyMatch[1].trim();
    }

    return null;
  }

  public navigateToSponsorOnboardingForClaim(shortId: string): Promise<boolean> {
    return this.navigateToSponsorOnboardingForClaimPath(this.buildClaimPath(shortId));
  }

  public navigateToSponsorOnboardingForClaimPath(claimPath: string): Promise<boolean> {
    return this.authNavigation.navigateToSponsorOnboarding(claimPath);
  }

  public navigateToSponsorRegisterForClaim(shortId: string): Promise<boolean> {
    return this.navigateToSponsorOnboardingForClaim(shortId);
  }

  public navigateToSponsorRegisterForClaimPath(claimPath: string): Promise<boolean> {
    return this.navigateToSponsorOnboardingForClaimPath(claimPath);
  }

  public navigateToSponsorLoginForClaimPath(claimPath: string): Promise<boolean> {
    return this.authNavigation.navigateToLogin(claimPath);
  }

  public storeClaimRedirect(claimPath: string): void {
    this.authNavigation.storeRedirectToIfPresent(claimPath);
  }

  public navigateToOffers(): Promise<boolean> {
    return this.appRouteNavigation.navigate(SPONSOR_ROUTES.offers);
  }

  public navigateToOffersWithClaimedOffer(offerId: string): Promise<boolean> {
    return this.appRouteNavigation.navigate(SPONSOR_ROUTES.offers, {
      queryParams: { [SponsorOffersService.openDrawerFromUrlOfferIdQueryParam]: offerId },
    });
  }

  public clearClaimRedirectIfMatching(claimPath: string): void {
    this.authService.discardStoredRedirectIfMatching(claimPath);
  }

  public consumeClaimRedirectIfMatching(claimPath: string): void {
    const stored = this.authService.peekPostLoginRedirect();
    if (stored === this.authService.sanitizePostLoginRedirect(claimPath)) {
      this.authService.consumePostLoginRedirect();
    }
  }
}
